// src/pages/VerifyEmail.jsx

import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "../firebase/analytics";
import { auth } from "../firebase/config";
import { sendEmailVerification } from "firebase/auth";
import { finalizeEmployerAfterEmailVerified } from "../firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/VerifyEmail.css";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  // Prefer data passed via navigate state; fallback to sessionStorage
  const stateData = location.state;
  const stored = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem("pendingEmployerProfile") || "{}",
      );
    } catch {
      return {};
    }
  }, []);
  const profile = stateData || stored;

  const [, setChecking] = useState(true);
  const [verified, setVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    trackEvent("verify_email_view", {
      page: "verify_email",
    });
  }, []);

  useEffect(() => {
    if (!auth.currentUser) {
      navigate("/"); // not logged in
      return;
    }

    let timer;
    const check = async () => {
      try {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          trackEvent("email_verified", {
            page: "verify_email",
          });

          setVerified(true);
          clearInterval(timer);
          setChecking(false);
        } else {
          setVerified(false);
          setChecking(false);
        }
      } catch (e) {
        setError(e.message);
        setChecking(false);
      }
    };

    // initial + poll every 3s
    check();
    timer = setInterval(check, 3000);
    return () => clearInterval(timer);
  }, [navigate]);

  const resend = async () => {
    if (!auth.currentUser) return;
    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser, {
        url: `${window.location.origin}/verify-email`,
      });
      trackEvent("verification_email_resend", {
        page: "verify_email",
      });
    } catch (e) {
      setError("Could not resend verification email.");
    } finally {
      setResending(false);
    }
  };

  const continueToSetup = async () => {
    try {
      await finalizeEmployerAfterEmailVerified(profile);
      trackEvent("company_setup_continue", {
        page: "verify_email",
      });
      // Ensure the SDK has the freshest auth state and tokens
      await auth.currentUser?.reload();
      await auth.currentUser?.getIdToken(true);

      // ⬇️ Tell CompanySetup to hard-refresh once on first load
      localStorage.setItem("needsCompanySetupRefresh", "true");

      // cleanup the temporary stash
      sessionStorage.removeItem("pendingEmployerProfile");

      // Use replace to avoid back-nav glitches
      navigate("/company-setup", { replace: true });
    } catch (e) {
      setError(e.message || "Could not finalize account.");
    }
  };

  const email = auth.currentUser?.email || profile.email || "";

  return (
    <div className="verify-wrapper">
      <h1>Check your inbox</h1>
      <p>
        We sent a verification link to <b>{email}</b>.
      </p>
      <p>Click the link in your email, then come back here.</p>
      <p className="verify-disclaimer">
        Don’t see the email? Check your spam or junk folder. It may take a few
        minutes to arrive.
      </p>

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button onClick={resend} disabled={resending}>
          {resending ? "Sending..." : "Resend verification email"}
        </button>

        <button
          onClick={continueToSetup}
          disabled={!verified}
          className={verified ? "primary" : "disabled"}
        >
          {verified
            ? "Continue to Company Setup"
            : "Waiting for verification..."}
        </button>
      </div>

      <small>This page will auto-detect when your email is verified</small>
    </div>
  );
}
